/**
 * VcStorage — Platform-aware key/value storage abstraction.
 *
 * Uses Capacitor Preferences on native (iOS/Android) for reliable
 * persistence, falls back to localStorage on web.
 *
 * All methods return Promises for a unified async API.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

var _isNative = false;
try {
  _isNative = Capacitor.isNativePlatform();
} catch (e) {
  _isNative = false;
}

export var VcStorage = {
  isNative: function () {
    return _isNative;
  },

  /**
   * Get a string value by key.
   * @param {string} key
   * @returns {Promise<string|null>}
   */
  get: function (key) {
    if (_isNative) {
      return Preferences.get({ key: key }).then(function (result) {
        return result.value;
      });
    }
    try {
      var val = window.localStorage.getItem(key);
      return Promise.resolve(val);
    } catch (e) {
      return Promise.resolve(null);
    }
  },

  /**
   * Get a parsed JSON value by key.
   * @param {string} key
   * @returns {Promise<any|null>}
   */
  getJSON: function (key) {
    return VcStorage.get(key).then(function (val) {
      if (val == null) return null;
      try {
        return JSON.parse(val);
      } catch (e) {
        return null;
      }
    });
  },

  /**
   * Set a string value by key.
   * @param {string} key
   * @param {string} value
   * @returns {Promise<void>}
   */
  set: function (key, value) {
    if (_isNative) {
      return Preferences.set({ key: key, value: value });
    }
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      // quota exceeded or unavailable
    }
    return Promise.resolve();
  },

  /**
   * Set a JSON-serializable value by key.
   * @param {string} key
   * @param {any} value
   * @returns {Promise<void>}
   */
  setJSON: function (key, value) {
    return VcStorage.set(key, JSON.stringify(value));
  },

  /**
   * Remove a key.
   * @param {string} key
   * @returns {Promise<void>}
   */
  remove: function (key) {
    if (_isNative) {
      return Preferences.remove({ key: key });
    }
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
    return Promise.resolve();
  },

  /**
   * Clear all stored data.
   * @returns {Promise<void>}
   */
  clear: function () {
    if (_isNative) {
      return Preferences.clear();
    }
    try {
      window.localStorage.clear();
    } catch (e) {
      // ignore
    }
    return Promise.resolve();
  }
};
